// ✅ src/pages/VehicleV2/JobCardEntryDetail.jsx  (UPDATED FULL FILE)
// Backend expected:
//   app.use("/api/jobCardEntryDetail", jobCardEntryDetail);
//
// APIs used:
//   GET  https://nissi-india.com/api/jobCardEntryDetail/init?loginUserId=&branchId=&branchType=&vehicleId=
//        -> returns { success:true, header:{...}, jobcards:[...], details:[...] }
//   POST https://nissi-india.com/api/jobCardEntryDetail/submit
//        body: {
//          loginUserId, branchId, branchType, vehicleId,
//          rows:[{ jobCardEntryId, inspectedBy, remedy, fromDate, inTime, toDate, outTime, hrs, maintenanceBy }]
//        }
//
// ✅ Updated pattern like latest screens:
// - common localStorage/sessionStorage header pattern
// - inventory header support
// - useMemo + useCallback pattern
// - loading + pageLoading + saving states
// - vehicleId from query/localStorage
// - same UI structure style
// - safe mapping for init response
// - client-side pagination for BOTH tables
// - select all for current page
// - spare details navigation support

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaArrowLeft, FaSave, FaUndo } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/jobCardEntryDetails";

const toText = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v);
};

const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const clean = (v) => String(v ?? "").trim();

const pad2 = (n) => String(n).padStart(2, "0");

const parseDDMMYYYY = (s) => {
  const parts = String(s || "").split("/");
  if (parts.length !== 3) return null;

  const [dd, mm, yyyy] = parts;
  const d = Number(dd);
  const m = Number(mm);
  const y = Number(yyyy);

  if (!Number.isFinite(d) || !Number.isFinite(m) || !Number.isFinite(y)) {
    return null;
  }

  const dt = new Date(y, m - 1, d);
  if (
    dt.getFullYear() !== y ||
    dt.getMonth() !== m - 1 ||
    dt.getDate() !== d
  ) {
    return null;
  }

  return dt;
};

const formatDDMMYYYY = (dateObj) => {
  if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return "";
  return `${pad2(dateObj.getDate())}/${pad2(
    dateObj.getMonth() + 1
  )}/${dateObj.getFullYear()}`;
};

const parseAnyDateToDDMMYYYY = (raw) => {
  const val = clean(raw);
  if (!val) return "";

  const ddmmyyyy = parseDDMMYYYY(val);
  if (ddmmyyyy) return formatDDMMYYYY(ddmmyyyy);

  const dt = new Date(val);
  if (!Number.isNaN(dt.getTime())) {
    return formatDDMMYYYY(dt);
  }

  return val;
};

const JobCardEntryDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ shared localStorage / sessionStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "8";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // ✅ get vehicleId from query/localStorage
  const vehicleId = useMemo(() => {
    const sp = new URLSearchParams(location?.search || "");
    const fromQuery = toInt(sp.get("vehicleId") || sp.get("vehicleid"));
    if (fromQuery) return fromQuery;

    return toInt(localStorage.getItem("vehicleId") || 0);
  }, [location]);

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [headerInfo, setHeaderInfo] = useState({
    jobCardNo: "",
    vehicleName: "",
    vehicleRegNo: "",
    vehicleCode: "",
    vehicleModel: "",
  });

  const [jobcards, setJobcards] = useState([]);
  const [details, setDetails] = useState([]);

  // top table pagination
  const [page1, setPage1] = useState(1);
  const [limit1, setLimit1] = useState(10);

  // bottom table pagination
  const [page2, setPage2] = useState(1);
  const [limit2, setLimit2] = useState(10);

  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // ---- styles ----
  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const smallInputClass = `w-full px-2 py-1 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const cardClass = `p-6 mt-6 rounded-2xl shadow-xl border ${
    isDark
      ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
      : `bg-white ${currentSkin.glass} text-black`
  }`;

  const mapHeader = useCallback((data) => {
    const h = data?.header || data || {};
    return {
      jobCardNo: toText(h?.jobCardNo ?? h?.fld_jobcardno),
      vehicleName: toText(h?.vehicleName ?? h?.fld_vehicle_name),
      vehicleRegNo: toText(h?.vehicleRegNo ?? h?.fld_vehicle_regno),
      vehicleCode: toText(h?.vehicleCode ?? h?.fld_vehicle_code),
      vehicleModel: toText(h?.vehicleModel ?? h?.fld_vehicle_model),
    };
  }, []);

  const mapTopRow = useCallback(
    (r) => ({
      jobCardEntryId: toInt(
        r?.jobCardEntryId ?? r?.fld_jobcardentryid ?? r?.id,
        0
      ),
      vehicleId: toInt(r?.vehicleId ?? r?.fld_vehicleid ?? vehicleId, 0),

      jobCardNo: toText(r?.jobCardNo ?? r?.fld_jobcardno),
      entryDate: toText(r?.entryDate ?? r?.fld_entrydate1 ?? r?.fld_entrydate),
      vehicleName: toText(r?.vehicleName ?? r?.fld_vehicle_name),
      vehicleRegNo: toText(r?.vehicleRegNo ?? r?.fld_vehicle_regno),
      location: toText(r?.location ?? r?.fld_Worklocationname),
      hrKm: toText(r?.hrKm ?? r?.fld_hrs ?? r?.fld_hrmtrreading),
      status: toText(r?.status ?? r?.fld_status),
      natureOfComplaint: toText(
        r?.natureOfComplaint ?? r?.fld_natureofcomplaint
      ),
      complaintAttendedBy: toText(
        r?.complaintAttendedBy ?? r?.fld_complaintattendedby
      ),
      sparesCount: toInt(r?.sparesCount ?? r?.fld_spares ?? r?.spares, 0),

      inspectedBy: toText(r?.inspectedBy ?? r?.fld_inspectedby),
      remedy: toText(r?.remedy ?? r?.fld_remedy),
      fromDate: parseAnyDateToDDMMYYYY(
        r?.fromDate ?? r?.fld_maintainfromdate1 ?? r?.fld_maintainfromdate
      ),
      inTime: toText(r?.inTime ?? r?.fld_maintainintime),
      toDate: parseAnyDateToDDMMYYYY(
        r?.toDate ?? r?.fld_maintaintodate1 ?? r?.fld_maintaintodate
      ),
      outTime: toText(r?.outTime ?? r?.fld_maintainouttime),
      hrs: toText(r?.hrs ?? r?.fld_maintainhrs),
      maintenanceBy: toText(r?.maintenanceBy ?? r?.fld_maintenanceby),
      _raw: r,
    }),
    [vehicleId]
  );

  const mapDetailRow = useCallback((r) => {
    return {
      jobCardEntryId: toInt(
        r?.jobCardEntryId ?? r?.fld_jobcardentryid ?? r?.id,
        0
      ),
      jobCardNo: toText(r?.jobCardNo ?? r?.fld_jobcardno),
      entryDate: toText(r?.entryDate ?? r?.fld_entrydate1 ?? r?.fld_entrydate),
      vehicleName: toText(r?.vehicleName ?? r?.fld_vehicle_name),
      vehicleRegNo: toText(r?.vehicleRegNo ?? r?.fld_vehicle_regno),
      location: toText(r?.location ?? r?.fld_Worklocationname),
      hrKm: toText(r?.hrKm ?? r?.fld_hrs ?? r?.fld_hrmtrreading),
      status: toText(r?.status ?? r?.fld_status),
      natureOfComplaint: toText(
        r?.natureOfComplaint ?? r?.fld_natureofcomplaint
      ),
      complaintAttendedBy: toText(
        r?.complaintAttendedBy ?? r?.fld_complaintattendedby
      ),

      inspectedBy: toText(r?.inspectedBy ?? r?.fld_inspectedby),
      remedy: toText(r?.remedy ?? r?.fld_remedy),
      fromDate: parseAnyDateToDDMMYYYY(
        r?.fromDate ?? r?.fld_maintainfromdate1 ?? r?.fld_maintainfromdate
      ),
      inTime: toText(r?.inTime ?? r?.fld_maintainintime),
      toDate: parseAnyDateToDDMMYYYY(
        r?.toDate ?? r?.fld_maintaintodate1 ?? r?.fld_maintaintodate
      ),
      outTime: toText(r?.outTime ?? r?.fld_maintainouttime),
      hrs: toText(r?.hrs ?? r?.fld_maintainhrs),
      maintenanceBy: toText(r?.maintenanceBy ?? r?.fld_maintenanceby),
      spareDetails: toText(r?.spareDetails ?? r?.fld_sparedetails ?? "View"),
      _raw: r,
    };
  }, []);

  /* ------------------------- INIT LOAD ------------------------- */
  const loadInit = useCallback(async () => {
    try {
      if (!toInt(vehicleId, 0)) {
        toast.warn("vehicleId not found");
        return;
      }

      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          loginUserId: toInt(loginUserId, 0),
          branchId: toInt(branchId, 0),
          branchType: clean(branchType),
          vehicleId: toInt(vehicleId, 0),
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Init failed");
      }

      setHeaderInfo(mapHeader(data));

      const topRows = Array.isArray(data?.jobcards)
        ? data.jobcards.map(mapTopRow)
        : [];
      const bottomRows = Array.isArray(data?.details)
        ? data.details.map(mapDetailRow)
        : [];

      setJobcards(topRows);
      setDetails(bottomRows);

      setSelectedIds([]);
      setSelectAll(false);
      setPage1(1);
      setPage2(1);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err?.message || "Init failed"
      );
    } finally {
      setPageLoading(false);
    }
  }, [
    loginUserId,
    branchId,
    branchType,
    vehicleId,
    commonHeaders,
    mapHeader,
    mapTopRow,
    mapDetailRow,
  ]);

  /* ------------------------- PAGINATION ------------------------- */
  const totalPages1 = useMemo(() => {
    const t = Math.ceil(jobcards.length / limit1);
    return t > 0 ? t : 1;
  }, [jobcards.length, limit1]);

  const totalPages2 = useMemo(() => {
    const t = Math.ceil(details.length / limit2);
    return t > 0 ? t : 1;
  }, [details.length, limit2]);

  const pageRows1 = useMemo(() => {
    const start = (page1 - 1) * limit1;
    return jobcards.slice(start, start + limit1);
  }, [jobcards, page1, limit1]);

  const pageRows2 = useMemo(() => {
    const start = (page2 - 1) * limit2;
    return details.slice(start, start + limit2);
  }, [details, page2, limit2]);

  const paginationText1 = useMemo(() => {
    const total = jobcards.length;
    const from = total === 0 ? 0 : (page1 - 1) * limit1 + 1;
    const to = Math.min(total, page1 * limit1);
    return `${from}-${to} of ${total}`;
  }, [jobcards.length, page1, limit1]);

  const paginationText2 = useMemo(() => {
    const total = details.length;
    const from = total === 0 ? 0 : (page2 - 1) * limit2 + 1;
    const to = Math.min(total, page2 * limit2);
    return `${from}-${to} of ${total}`;
  }, [details.length, page2, limit2]);

  const canPrev1 = page1 > 1;
  const canNext1 = page1 < totalPages1;
  const canPrev2 = page2 > 1;
  const canNext2 = page2 < totalPages2;

  /* ------------------------- SELECTION ------------------------- */
  const toggleSelect = useCallback((jobCardEntryId) => {
    setSelectedIds((prev) =>
      prev.includes(jobCardEntryId)
        ? prev.filter((x) => x !== jobCardEntryId)
        : [...prev, jobCardEntryId]
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    const idsOnPage = pageRows1.map((r) => r.jobCardEntryId).filter(Boolean);

    if (selectAll) {
      setSelectedIds((prev) => prev.filter((id) => !idsOnPage.includes(id)));
      setSelectAll(false);
    } else {
      setSelectedIds((prev) => {
        const set = new Set(prev);
        idsOnPage.forEach((id) => set.add(id));
        return Array.from(set);
      });
      setSelectAll(true);
    }
  }, [pageRows1, selectAll]);

  useEffect(() => {
    const idsOnPage = pageRows1.map((r) => r.jobCardEntryId).filter(Boolean);
    setSelectAll(
      idsOnPage.length > 0 && idsOnPage.every((id) => selectedIds.includes(id))
    );
  }, [pageRows1, selectedIds]);

  /* ------------------------- EDITABLE GRID ------------------------- */
  const updateRowField = useCallback((jobCardEntryId, field, value) => {
    setJobcards((prev) =>
      prev.map((r) =>
        r.jobCardEntryId === jobCardEntryId ? { ...r, [field]: value } : r
      )
    );
  }, []);

  /* ------------------------- VALIDATION ------------------------- */
  const validateSelectedRows = useCallback((rows) => {
    if (!Array.isArray(rows) || rows.length === 0) {
      toast.warn("Please select at least one row");
      return false;
    }

    for (const row of rows) {
      if (clean(row.fromDate) && !parseDDMMYYYY(row.fromDate)) {
        toast.warn(`Invalid From Date for Job Card No ${row.jobCardNo || ""}`);
        return false;
      }

      if (clean(row.toDate) && !parseDDMMYYYY(row.toDate)) {
        toast.warn(`Invalid To Date for Job Card No ${row.jobCardNo || ""}`);
        return false;
      }
    }

    return true;
  }, []);

  /* ------------------------- ACTIONS ------------------------- */
  const handleSubmit = async () => {
    try {
      const selectedRows = jobcards.filter((r) =>
        selectedIds.includes(r.jobCardEntryId)
      );

      if (!validateSelectedRows(selectedRows)) return;

      setSaving(true);

      const body = {
        loginUserId: toInt(loginUserId, 0),
        branchId: toInt(branchId, 0),
        branchType: clean(branchType),
        vehicleId: toInt(vehicleId, 0),
        rows: selectedRows.map((r) => ({
          jobCardEntryId: toInt(r.jobCardEntryId, 0),
          inspectedBy: clean(r.inspectedBy),
          remedy: clean(r.remedy),
          fromDate: clean(r.fromDate),
          inTime: clean(r.inTime),
          toDate: clean(r.toDate),
          outTime: clean(r.outTime),
          hrs: clean(r.hrs),
          maintenanceBy: clean(r.maintenanceBy),
        })),
      };

      const { data } = await axios.post(`${API_BASE}/submit`, body, {
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Submit failed");
      }

      toast.success(data?.message || "Submitted successfully");
      await loadInit();
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err?.message || "Submit failed"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = useCallback(() => {
    setJobcards((prev) =>
      prev.map((r) => ({
        ...r,
        inspectedBy: "",
        remedy: "",
        fromDate: "",
        inTime: "",
        toDate: "",
        outTime: "",
        hrs: "",
        maintenanceBy: "",
      }))
    );
    setSelectedIds([]);
    setSelectAll(false);
  }, []);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleSpareDetails = useCallback(
    (row) => {
      const id = toInt(row?.fld_jobcardentryid, 0);
      if (!id) {
        toast.error("JobCardEntryId not found");
        return;
      }

      navigate(`/vehicle/SparesDetails?jobCardEntryId=${encodeURIComponent(id)}`);
    },
    [navigate]
  );

  /* ------------------------- EFFECTS ------------------------- */
  useEffect(() => {
    loadInit();
  }, [loadInit]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">JOB CARD ENTRY DETAIL</span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Top header row */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBack}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
                disabled={loading || pageLoading || saving}
              >
                <FaArrowLeft />
                Back
              </button>

              <h1
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Job Card Entry Detail
              </h1>
            </div>

            <div className={`text-sm ${isDark ? "text-gray-200" : "text-gray-700"}`}>
              INFRA SCREEN : B-29/2
            </div>
          </div>

          {/* Header area */}
          <div
            className={`rounded-xl p-6 mb-6 border ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-2">
                <label className={labelClass}>Job Card No</label>
              </div>
              <div className="md:col-span-4">
                <div className={`${isDark ? "text-white" : "text-gray-900"} font-medium`}>
                  {headerInfo.jobCardNo || "-"}
                </div>
              </div>

              <div className="md:col-span-2">
                <label className={labelClass}>Vehicle Name</label>
              </div>
              <div className="md:col-span-4">
                <div className={`${isDark ? "text-white" : "text-gray-900"} font-medium`}>
                  {headerInfo.vehicleName || "-"}
                </div>
              </div>
            </div>

            <div className="h-5" />

            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-2">
                <label className={labelClass}>Vehicle Reg No</label>
              </div>
              <div className="md:col-span-4">
                <div className={`${isDark ? "text-white" : "text-gray-900"} font-medium`}>
                  {headerInfo.vehicleRegNo || "-"}
                </div>
              </div>

              <div className="md:col-span-2">
                <label className={labelClass}>Vehicle Code</label>
              </div>
              <div className="md:col-span-4">
                <div className={`${isDark ? "text-white" : "text-gray-900"} font-medium`}>
                  {headerInfo.vehicleCode || "-"}
                </div>
              </div>
            </div>

            <div className="h-5" />

            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-2">
                <label className={labelClass}>Vehicle Model</label>
              </div>
              <div className="md:col-span-4">
                <div className={`${isDark ? "text-white" : "text-gray-900"} font-medium`}>
                  {headerInfo.vehicleModel || "-"}
                </div>
              </div>
            </div>
          </div>

          {/* ---------------- TOP GRID ---------------- */}
          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="overflow-x-auto">
              <table className="min-w-[2600px] w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold">S.No</th>
                    <th className="px-4 py-4 text-center font-semibold">Job Card No</th>
                    <th className="px-4 py-4 text-center font-semibold">Entry Date</th>
                    <th className="px-4 py-4 text-center font-semibold">Vehicle Name</th>
                    <th className="px-4 py-4 text-center font-semibold">Vehicle Reg No</th>
                    <th className="px-4 py-4 text-center font-semibold">Location</th>
                    <th className="px-4 py-4 text-center font-semibold">Hr/Km</th>
                    <th className="px-4 py-4 text-center font-semibold">Status</th>
                    <th className="px-4 py-4 text-center font-semibold">Nature Of Complaint</th>
                    <th className="px-4 py-4 text-center font-semibold">
                      Complaint Attended By
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">Inspected By</th>
                    <th className="px-4 py-4 text-center font-semibold">Remedy</th>
                    <th className="px-4 py-4 text-center font-semibold">From Date</th>
                    <th className="px-4 py-4 text-center font-semibold">IN Time</th>
                    <th className="px-4 py-4 text-center font-semibold">To Date</th>
                    <th className="px-4 py-4 text-center font-semibold">Out Time</th>
                    <th className="px-4 py-4 text-center font-semibold">Hrs</th>
                    <th className="px-4 py-4 text-center font-semibold">Maintenance By</th>
                    <th className="px-4 py-4 text-center font-semibold">Spare Details</th>
                    <th className="px-4 py-4 text-center font-semibold">
                      <div className="flex items-center justify-center gap-2">
                        <span>Select All</span>
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                          disabled={loading || pageLoading || saving}
                        />
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading || pageLoading ? (
                    <tr>
                      <td colSpan={20} className="text-center py-10">
                        Loading...
                      </td>
                    </tr>
                  ) : pageRows1.length === 0 ? (
                    <tr>
                      <td colSpan={20} className="text-center py-10">
                        No records found
                      </td>
                    </tr>
                  ) : (
                    pageRows1.map((row, idx) => {
                      const rid = row.jobCardEntryId;
                      return (
                        <tr
                          key={rid || idx}
                          className={`transition-colors duration-200 ${
                            isDark ? "bg-gray-900" : "bg-white"
                          }`}
                        >
                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {(page1 - 1) * limit1 + idx + 1}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.jobCardNo}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.entryDate}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.vehicleName}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.vehicleRegNo}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.location}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.hrKm}
                          </td>

                          <td className="px-4 py-4 text-sm text-center">
                            {row.status ? (
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                                {row.status}
                              </span>
                            ) : (
                              ""
                            )}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.natureOfComplaint}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.complaintAttendedBy}
                          </td>

                          <td className="px-3 py-3 min-w-[180px]">
                            <input
                              type="text"
                              value={row.inspectedBy}
                              onChange={(e) =>
                                updateRowField(rid, "inspectedBy", e.target.value)
                              }
                              className={smallInputClass}
                              placeholder="Inspected By"
                              disabled={saving || loading || pageLoading}
                            />
                          </td>

                          <td className="px-3 py-3 min-w-[180px]">
                            <input
                              type="text"
                              value={row.remedy}
                              onChange={(e) =>
                                updateRowField(rid, "remedy", e.target.value)
                              }
                              className={smallInputClass}
                              placeholder="Remedy"
                              disabled={saving || loading || pageLoading}
                            />
                          </td>

                          <td className="px-3 py-3 min-w-[130px]">
                            <input
                              type="text"
                              value={row.fromDate}
                              onChange={(e) =>
                                updateRowField(rid, "fromDate", e.target.value)
                              }
                              className={smallInputClass}
                              placeholder="dd/MM/yyyy"
                              disabled={saving || loading || pageLoading}
                            />
                          </td>

                          <td className="px-3 py-3 min-w-[100px]">
                            <input
                              type="text"
                              value={row.inTime}
                              onChange={(e) =>
                                updateRowField(rid, "inTime", e.target.value)
                              }
                              className={smallInputClass}
                              placeholder="IN"
                              disabled={saving || loading || pageLoading}
                            />
                          </td>

                          <td className="px-3 py-3 min-w-[130px]">
                            <input
                              type="text"
                              value={row.toDate}
                              onChange={(e) =>
                                updateRowField(rid, "toDate", e.target.value)
                              }
                              className={smallInputClass}
                              placeholder="dd/MM/yyyy"
                              disabled={saving || loading || pageLoading}
                            />
                          </td>

                          <td className="px-3 py-3 min-w-[100px]">
                            <input
                              type="text"
                              value={row.outTime}
                              onChange={(e) =>
                                updateRowField(rid, "outTime", e.target.value)
                              }
                              className={smallInputClass}
                              placeholder="OUT"
                              disabled={saving || loading || pageLoading}
                            />
                          </td>

                          <td className="px-3 py-3 min-w-[100px]">
                            <input
                              type="text"
                              value={row.hrs}
                              onChange={(e) =>
                                updateRowField(rid, "hrs", e.target.value)
                              }
                              className={smallInputClass}
                              placeholder="Hrs"
                              disabled={saving || loading || pageLoading}
                            />
                          </td>

                          <td className="px-3 py-3 min-w-[180px]">
                            <input
                              type="text"
                              value={row.maintenanceBy}
                              onChange={(e) =>
                                updateRowField(rid, "maintenanceBy", e.target.value)
                              }
                              className={smallInputClass}
                              placeholder="Maintenance By"
                              disabled={saving || loading || pageLoading}
                            />
                          </td>

                          <td className="px-4 py-4 text-center min-w-[120px]">
                            <button
                              onClick={() => handleSpareDetails(row)}
                              className="text-blue-600 hover:text-blue-800 font-semibold underline text-sm"
                              type="button"
                              disabled={saving || loading || pageLoading}
                            >
                              {toInt(row.sparesCount, 0)}
                            </button>
                          </td>

                          <td className="px-4 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(rid)}
                              onChange={() => toggleSelect(rid)}
                              disabled={saving || loading || pageLoading}
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Top grid footer */}
            <div
              className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 px-4 py-3 border-t ${
                isDark ? "border-gray-700 bg-gray-900" : "border-gray-200 bg-white"
              }`}
            >
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                {paginationText1}
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={limit1}
                  onChange={(e) => {
                    setLimit1(Number(e.target.value));
                    setPage1(1);
                  }}
                  disabled={loading || pageLoading || saving}
                  className={inputClass}
                  style={{ width: 120 }}
                >
                  {[10, 20, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n} / page
                    </option>
                  ))}
                </select>

                <button
                  disabled={!canPrev1 || loading || pageLoading || saving}
                  onClick={() => setPage1((p) => Math.max(1, p - 1))}
                  className={`px-4 py-2 rounded-lg border ${
                    canPrev1 && !loading && !pageLoading && !saving
                      ? "hover:bg-gray-50 dark:hover:bg-gray-800"
                      : "opacity-50 cursor-not-allowed"
                  } ${
                    isDark ? "border-gray-700 text-gray-200" : "border-gray-300 text-gray-700"
                  }`}
                >
                  Prev
                </button>

                <div className={`text-sm px-2 ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                  Page {page1} / {totalPages1}
                </div>

                <button
                  disabled={!canNext1 || loading || pageLoading || saving}
                  onClick={() => setPage1((p) => Math.min(totalPages1, p + 1))}
                  className={`px-4 py-2 rounded-lg border ${
                    canNext1 && !loading && !pageLoading && !saving
                      ? "hover:bg-gray-50 dark:hover:bg-gray-800"
                      : "opacity-50 cursor-not-allowed"
                  } ${
                    isDark ? "border-gray-700 text-gray-200" : "border-gray-300 text-gray-700"
                  }`}
                >
                  Next
                </button>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={handleReset}
                  type="button"
                  disabled={saving || loading || pageLoading}
                  className={`flex items-center gap-2 px-5 py-2 rounded-lg font-semibold shadow border transition-all duration-200 ${
                    isDark
                      ? "border-gray-600 text-white hover:bg-gray-700"
                      : "border-gray-300 text-gray-800 hover:bg-gray-100"
                  } ${
                    saving || loading || pageLoading
                      ? "opacity-70 cursor-not-allowed"
                      : ""
                  }`}
                >
                  <FaUndo />
                  Reset
                </button>

                <button
                  onClick={handleSubmit}
                  disabled={
                    saving || loading || pageLoading || selectedIds.length === 0
                  }
                  className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-5 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    saving || loading || pageLoading || selectedIds.length === 0
                      ? "opacity-70 cursor-not-allowed"
                      : ""
                  }`}
                >
                  <FaSave />
                  {saving ? "Saving..." : "Submit"}
                </button>
              </div>
            </div>
          </div>

          {/* ---------------- BOTTOM GRID ---------------- */}
          <div className="mt-8">
            <h2
              className={`text-xl font-bold mb-3 ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              Job Card Entry Detail Listing
            </h2>

            <div
              className={`rounded-xl shadow-sm border overflow-hidden ${
                isDark ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <div className="overflow-x-auto">
                <table className="min-w-[2400px] w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold">S.No</th>
                      <th className="px-4 py-4 text-center font-semibold">Job Card No</th>
                      <th className="px-4 py-4 text-center font-semibold">Entry Date</th>
                      <th className="px-4 py-4 text-center font-semibold">Vehicle Name</th>
                      <th className="px-4 py-4 text-center font-semibold">Vehicle Reg No</th>
                      <th className="px-4 py-4 text-center font-semibold">Location</th>
                      <th className="px-4 py-4 text-center font-semibold">Hr/Km</th>
                      <th className="px-4 py-4 text-center font-semibold">Status</th>
                      <th className="px-4 py-4 text-center font-semibold">Nature Of Complaint</th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Complaint Attended By
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">Inspected By</th>
                      <th className="px-4 py-4 text-center font-semibold">Remedy</th>
                      <th className="px-4 py-4 text-center font-semibold">From Date</th>
                      <th className="px-4 py-4 text-center font-semibold">In Time</th>
                      <th className="px-4 py-4 text-center font-semibold">To Date</th>
                      <th className="px-4 py-4 text-center font-semibold">Out Time</th>
                      <th className="px-4 py-4 text-center font-semibold">Hrs</th>
                      <th className="px-4 py-4 text-center font-semibold">Maintenance By</th>
                      <th className="px-4 py-4 text-center font-semibold">Spare Details</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {pageLoading ? (
                      <tr>
                        <td colSpan={19} className="text-center py-10">
                          Loading...
                        </td>
                      </tr>
                    ) : pageRows2.length === 0 ? (
                      <tr>
                        <td colSpan={19} className="text-center py-10">
                          No Records Found!
                        </td>
                      </tr>
                    ) : (
                      pageRows2.map((row, idx) => (
                        <tr
                          key={`${row.jobCardEntryId}-${idx}`}
                          className={`transition-colors duration-200 ${
                            isDark ? "bg-gray-900" : "bg-white"
                          }`}
                        >
                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {(page2 - 1) * limit2 + idx + 1}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.jobCardNo}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.entryDate}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.vehicleName}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.vehicleRegNo}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.location}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.hrKm}
                          </td>

                          <td className="px-4 py-4 text-sm text-center">
                            {row.status ? (
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                                {row.status}
                              </span>
                            ) : (
                              ""
                            )}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.natureOfComplaint}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.complaintAttendedBy}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.inspectedBy}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.remedy}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.fromDate}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.inTime}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.toDate}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.outTime}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.hrs}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.maintenanceBy}
                          </td>

                          <td className="px-4 py-4 text-center">
                            <button
                              onClick={() => handleSpareDetails(row)}
                              className="text-blue-600 hover:text-blue-800 font-semibold underline text-sm"
                              type="button"
                              disabled={saving || loading || pageLoading}
                            >
                              {clean(row.spareDetails) || "View"}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Bottom pagination */}
              <div
                className={`flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-4 py-3 border-t ${
                  isDark ? "border-gray-700 bg-gray-900" : "border-gray-200 bg-white"
                }`}
              >
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  {paginationText2}
                </div>

                <div className="flex items-center gap-2">
                  <select
                    value={limit2}
                    onChange={(e) => {
                      setLimit2(Number(e.target.value));
                      setPage2(1);
                    }}
                    disabled={loading || pageLoading || saving}
                    className={inputClass}
                    style={{ width: 120 }}
                  >
                    {[10, 20, 50, 100].map((n) => (
                      <option key={n} value={n}>
                        {n} / page
                      </option>
                    ))}
                  </select>

                  <button
                    disabled={!canPrev2 || loading || pageLoading || saving}
                    onClick={() => setPage2((p) => Math.max(1, p - 1))}
                    className={`px-4 py-2 rounded-lg border ${
                      canPrev2 && !loading && !pageLoading && !saving
                        ? "hover:bg-gray-50 dark:hover:bg-gray-800"
                        : "opacity-50 cursor-not-allowed"
                    } ${
                      isDark ? "border-gray-700 text-gray-200" : "border-gray-300 text-gray-700"
                    }`}
                  >
                    Prev
                  </button>

                  <div className={`text-sm px-2 ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                    Page {page2} / {totalPages2}
                  </div>

                  <button
                    disabled={!canNext2 || loading || pageLoading || saving}
                    onClick={() => setPage2((p) => Math.min(totalPages2, p + 1))}
                    className={`px-4 py-2 rounded-lg border ${
                      canNext2 && !loading && !pageLoading && !saving
                        ? "hover:bg-gray-50 dark:hover:bg-gray-800"
                        : "opacity-50 cursor-not-allowed"
                    } ${
                      isDark ? "border-gray-700 text-gray-200" : "border-gray-300 text-gray-700"
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default JobCardEntryDetail;